import { useState } from "react"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ChevronRight, Plus, Send } from "lucide-react"
import { toast } from "sonner"
import type { CampaignDetail, CampaignSummary } from "@vienna/contracts"
import { CAMPAIGN_STATUS_LABELS } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Empty, Header, SearchBox, Stats, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { ProviderIcon } from "@/components/campaigns/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime, relativeTime } from "@/lib/format"
import { campaignsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/campaigns/")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(campaignsQuery(params.workspace)),
  component: CampaignList,
})

const TABS = ["All campaigns", "Draft", "Scheduled", "Completed", "Needs attention"] as const

function detailOf(c: CampaignSummary) {
  if (c.status === "scheduled" && c.scheduledAt) return formatDateTime(c.scheduledAt)
  return c.detail
}

/** Screen 24. */
function CampaignList() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const navigate = useNavigate()
  const { campaigns, metrics } = useSuspenseQuery(campaignsQuery(workspace.slug)).data
  const [search, setSearch] = useState("")
  const [tab, setTab] = useState<(typeof TABS)[number]>("All campaigns")
  const params = { workspace: workspace.slug }
  const rows = campaigns.filter((c) => `${c.name} ${c.segmentName ?? ""}`.toLowerCase().includes(search.toLowerCase()) && (tab === "All campaigns" || CAMPAIGN_STATUS_LABELS[c.status] === tab || (tab === "Completed" && c.status === "sending")))
  const create = useMutation({
    mutationFn: () => api<CampaignDetail>(`/w/${workspace.slug}/campaigns`, { method: "POST", body: JSON.stringify({ name: "New campaign" }) }),
    onSuccess: async (created) => navigate({ to: "/w/$workspace/campaigns/$campaignId/audience", params: { ...params, campaignId: created.id } }),
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not create the campaign"),
  })
  return (
    <div className="page-wrap au-root">
      <Header title="Campaigns" description="Create thoughtful messages. Keep every send accountable.">
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/reports" params={params}>
            View report
          </Link>
        </Button>
        {can("edit_automations") && (
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            <Plus /> Create campaign
          </Button>
        )}
      </Header>
      <Stats
        items={[
          { label: "Draft campaigns", value: String(metrics.drafts), detail: metrics.drafts ? "Ready to continue" : "Nothing in progress" },
          { label: "Scheduled", value: String(metrics.scheduled), detail: metrics.nextSendAt ? `Next send ${formatDateTime(metrics.nextSendAt)}` : "No sends planned" },
          { label: "Delivered this month", value: metrics.deliveredThisMonth.toLocaleString(), detail: "Confirmed by delivery receipts" },
          { label: "Needs attention", value: String(metrics.attention), detail: metrics.attention ? "Channel connection to review" : "Nothing to review" },
        ]}
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as (typeof TABS)[number])} className="mt-7">
        <TabsList variant="line" className="au-tab-line">
          {TABS.map((name) => (
            <TabsTrigger value={name} key={name}>
              {name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="au-toolbar">
        <SearchBox value={search} onChange={setSearch} placeholder="Search campaigns…" />
        <span className="ml-auto text-xs text-muted-foreground">
          {rows.length} campaign{rows.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="au-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Audience / timing</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Link to="/w/$workspace/campaigns/$campaignId" params={{ ...params, campaignId: c.id }} className="au-person text-left">
                    <span className="au-square-icon">
                      <Send />
                    </span>
                    <span>
                      <span className="block font-medium">{c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.segmentName ?? "Audience not chosen"}</span>
                    </span>
                  </Link>
                </TableCell>
                <TableCell>
                  <Status>{CAMPAIGN_STATUS_LABELS[c.status]}</Status>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <ProviderIcon provider={c.provider} className="size-3.5" /> {providerLabel(c.provider)}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">{detailOf(c)}</TableCell>
                <TableCell className="text-muted-foreground">{relativeTime(c.updatedAt)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon-sm" aria-label={`Open ${c.name}`} asChild>
                    <Link to="/w/$workspace/campaigns/$campaignId" params={{ ...params, campaignId: c.id }}>
                      <ChevronRight />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!rows.length && <Empty noun="campaigns" hint={campaigns.length ? "Try another search or tab." : "Create a campaign to reach an audience through one channel."} />}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Each campaign delivers through one channel. Audience membership doesn’t grant permission to message.</p>
    </div>
  )
}
